import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { FieldValues, SubmitHandler } from 'react-hook-form';
import TagManager from 'react-gtm-module';
import i18n from 'i18next';
import CloseIcon from '@mui/icons-material/Close';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import dabaduIcon from 'assets/images/trade-in/dabadu-icon.png';
import useQueryParams from 'hooks/UseQueryParams';
import DynamicStepperForm from 'sharedComponents/dynamicStepperForm';
import { StepConfigItem } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { DefaultModal } from 'sharedComponents/modal';
import { clearLeadCreation, postTradeInLeadCreation, resetStepCount } from 'store/TradeInState';
import { RootState } from 'store/store';
import { Lead } from 'pages/leadGen/LeadGenInterface';
import { BOOK_APPOINTMENT_MODAL_CLOSE, BOOK_APPOINTMENT_MODULE_NAME } from './BookAppointmentConstant';
import { BookAppointmentConfig } from './StepConfig';
import './BookAppointment.scss';

const BookAppointment: React.FC = () => {
  const { t } = useTranslation();

  const hasCalledMounted = React.useRef(false);

  const queryParams = useQueryParams();
  const [initialStep, setInitialStep] = useState(0);
  const [isSuccessModal, setIsSuccessModal] = useState(false);
  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const type = queryParams.get('type');
  const version = queryParams.get('version');

  const { authenticateClientData } = useSelector((state: RootState) => state.auth) || {};
  const { vehicleData } = useSelector((state: RootState) => state.digitalRetailingStore);
  const widgetData = authenticateClientData?.data?.widgetData || {};

  const leadSourceId = widgetData?.leadSourceId;
  const zipCode = widgetData?.zipCode;
  const leadTypeId = widgetData?.leadTypeId;

  const gtmID = widgetData?.gtmID || '';

  useEffect(() => {
    i18n.changeLanguage(authenticateClientData?.data?.widgetData?.language || 'en');

    if (gtmID) {
      // GTM initialize on component mount
      const tagManagerArgs = {
        gtmId: gtmID,
      };

      TagManager.initialize(tagManagerArgs);
    }
  }, [authenticateClientData, gtmID]);

  useEffect(() => {
    if (hasCalledMounted.current) return;
    hasCalledMounted.current = true;

    document.body.classList.add('tradein-modal-open');
    if (window.self !== window.top) {
      document.body.classList.add('iframe-html');
    }
  }, []);

  const onCloseModal = () => {
    window.parent.postMessage(
      { type: BOOK_APPOINTMENT_MODAL_CLOSE, data: { close: true }, res: { module: 'book-appointment' } },
      '*'
    );
    dispatch(resetStepCount());
    dispatch(clearLeadCreation());
    localStorage.setItem(`${BOOK_APPOINTMENT_MODULE_NAME}-currentStep`, '0');
    localStorage.removeItem(BOOK_APPOINTMENT_MODULE_NAME);
  };

  const clearLocalStorage = () => {
    localStorage.setItem(`${BOOK_APPOINTMENT_MODULE_NAME}-currentStep`, '0');
    localStorage.removeItem(BOOK_APPOINTMENT_MODULE_NAME);

    setIsSuccessModal(true);
    setInitialStep(1);
  };

  // Step configurations mapped by type and version
  const stepConfigsByTypeAndVersion: {
    [type: string]: { [version: string]: StepConfigItem[] };
  } = {
    banner: {
      '1.0': BookAppointmentConfig(t),
    },
    button: {
      '1.0': BookAppointmentConfig(t),
    },
    embed: {
      '1.0': BookAppointmentConfig(t),
    },
  };

  const handleFinalSubmit: SubmitHandler<FieldValues> = async (formData) => {
    // Perform the final API call using formData

    const {
      firstName,
      lastName,
      phone,
      email,
      message,
      testDriveAddress_country,
      testDriveAddress_lookupStreetName,
      testDriveAddress_lookupStreetNumber,
      testDriveAddress_city,
      testDriveAddress_postal,
      testDriveAddress_region,
      testDriveAddress,
      testDriveAddress_unit,
    } = formData;

    const updatedData: Lead = {
      firstName: firstName,
      lastName: lastName,
      phone: phone,
      email: email,
      leadSourceId: leadSourceId,
      birthDate: '1991-01-01',
      zipCode: zipCode,
      country: testDriveAddress_country,
      streetName: testDriveAddress_lookupStreetName,
      addressNumber: testDriveAddress_lookupStreetNumber,
      city: testDriveAddress_city,
      leadDetails: {
        message: message,
        City: testDriveAddress_city,
        'Postal Code': testDriveAddress_postal,
        Province: testDriveAddress_region,
        'Street Address': testDriveAddress?.label,
        'Car Year': '1001',
        unit: testDriveAddress_unit,
        addressNumber: testDriveAddress_lookupStreetNumber,
        VIN: vehicleData?.VIN,
        StockNumber: vehicleData?.StockNumber,
        Year: vehicleData?.Year,
        Make: vehicleData?.Make,
        Model: vehicleData?.Model,
        Trim: vehicleData?.Trim,
      },
      province: testDriveAddress_region,
    };

    console.log(updatedData);

    dispatch(
      postTradeInLeadCreation({
        data: updatedData,
        leadTypeID: leadTypeId,
      })
    ).then((res) => {
      if (Object.keys(res?.payload).length && res?.payload?.message) {
        clearLocalStorage();
      }
    });
  };

  // Function to get the step configuration based on type and version
  const getStepConfig = (type: string | null, version: string | null): StepConfigItem[] => {
    if (!type || !version) {
      console.error('Invalid type or version');
      return [];
    }

    const typeConfig = stepConfigsByTypeAndVersion[type];
    if (!typeConfig) {
      console.error('Unknown type:', type);
      return [];
    }

    const config = typeConfig[version];
    if (!config) {
      console.error('No configuration found for type "' + type + '" and version "' + version + '"');
      return [];
    }

    return config;
  };

  // Retrieve step configuration based on the type and version
  const stepConfig = getStepConfig(type, version);

  return (
    <>
      <div className='container'>
        <div className='trade-in-stepper'>
          <header className='trade-in-stepper__header'>
            <img src={dabaduIcon} alt='Dabadu' className='img-fluid' />
            {window.self !== window.top && (
              <span className='close' onClick={onCloseModal}>
                <CloseIcon />
              </span>
            )}
          </header>

          <main className='trade-in-stepper__body px-0'>
            {version != null ? (
              <DynamicStepperForm
                stepConfig={stepConfig}
                initialStep={initialStep}
                onFinish={handleFinalSubmit}
                moduleConfig={{
                  boxShadow: false,
                  name: BOOK_APPOINTMENT_MODULE_NAME,
                }}
              />
            ) : null}
          </main>
        </div>
      </div>

      <DefaultModal
        open={isSuccessModal}
        handleClose={() => {
          setIsSuccessModal(false);
          onCloseModal();
        }}
        customClass='lead-gen-thankyou-modal'
        childern={
          <div className='trade-in-stepper__otp-complete__content text-center'>
            <div className='trade-in-stepper__otp-complete__content__icon'>
              <TaskAltIcon color='primary' sx={{ fontSize: 60 }} />
            </div>
            <h4 className='trade-in-stepper__otp-complete__content__title'>
              {t('stepperFormTradeIn.tradeInSuccessfully')}
            </h4>
          </div>
        }
      />
    </>
  );
};

export default BookAppointment;
